import AsyncStorage from "@react-native-async-storage/async-storage";
import * as Haptics from "expo-haptics";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import {
  ArrowLeft,
  Bot,
  CalendarDays,
  Check,
  ChevronRight,
  CircleHelp,
  ClipboardCheck,
  FileText,
  Gift,
  Heart,
  MapPin,
  MessageCircle,
  PackageCheck,
  PenLine,
  Plus,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
  X,
} from "../../../components/lucide-icons";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AccessibilityInfo,
  ImageBackground,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import QRCode from "react-native-qrcode-svg";

import { AppShell } from "../../../components/layout";
import { semantic, spacing } from "../../../theme/tokens";
import { ErrorState, Field, LoadingState, Status } from "../../../components/ui";
import { isOnline } from "../../../lib/api";
import { getProductsByCategory } from "../data/demo-arrangement-catalog";
import { arrangementRoutes } from "../routes";
import { ArrangementProduct } from "../types";
import {
  createFamilySubmissionKey,
  FAMILY_DRAFT_STORAGE_KEY,
  FAMILY_SUBMISSION_STORAGE_KEY,
  familyStarterIsComplete,
  FamilyStarterErrors,
  FamilyStarterField,
  FamilyStarterInput,
  FamilySubmissionRecord,
  FamilySubmissionStatus,
  getSubmissionBlockReason,
  parseFamilySubmissionRecord,
  submissionBlockMessage,
  validateFamilyStarter,
} from "../utils/family-critical-flow";

const monarchHeroImage = require("../../../../assets/monarch-arrangement-hero.png");

type IconComponent = (props: { color: string; size: number; strokeWidth?: number }) => ReactNode;

type StarterForm = FamilyStarterInput;

type CategoryAnswer = {
  selected: string[];
  notes: string;
  needsHelp: boolean;
};

type StudioCategory = {
  id: string;
  title: string;
  shortTitle: string;
  summary: string;
  prompt: string;
  step: 1 | 2 | 3 | 4;
  icon: IconComponent;
  options: string[];
  helperFields: string[];
  productKinds?: ArrangementProduct["category"][];
};

const emptyStarter: StarterForm = {
  lovedFirstName: "",
  lovedLastName: "",
  dateOfBirth: "",
  dateOfPassing: "",
  plannerName: "",
  relationship: "",
  phone: "",
  email: "",
};

const progressSteps = [
  { number: "1", label: "Information" },
  { number: "2", label: "Service" },
  { number: "3", label: "Selections" },
  { number: "4", label: "Review" },
];

const categories: StudioCategory[] = [
  {
    id: "big_picture",
    title: "The Big Picture",
    shortTitle: "Big Picture",
    summary: "Choose the overall path before details are requested.",
    prompt: "What kind of arrangement should Monarch prepare around first?",
    step: 2,
    icon: ClipboardCheck,
    options: ["Traditional burial", "Cremation", "Natural or green burial", "Body donation", "Still deciding"],
    helperFields: ["Funeral home preference", "Bring loved one into care", "Director guidance"],
  },
  {
    id: "care",
    title: "Caring for Your Loved One",
    shortTitle: "Care",
    summary: "Capture viewing, clothing, jewelry, hair, and makeup wishes.",
    prompt: "Which care and preparation details should the director know?",
    step: 2,
    icon: Heart,
    options: ["Standard sanitary care", "Embalming or viewing-ready care", "Private family viewing", "Public viewing", "No viewing"],
    helperFields: ["Clothing", "Jewelry and glasses", "Hair and makeup photo", "Viewing preference"],
  },
  {
    id: "caskets_urns_keepsakes",
    title: "Caskets, Urns, Keepsakes",
    shortTitle: "Selections",
    summary: "Select items that feel right; Monarch confirms availability and fit later.",
    prompt: "Choose the items the family wants Monarch to review.",
    step: 3,
    icon: PackageCheck,
    options: ["Casket", "Urn", "Keepsake urns", "Cremation jewelry", "Grave vault"],
    helperFields: ["Material", "Interior color", "Keepsake quantity", "Return or place items"],
    productKinds: ["casket", "urn", "keepsake"],
  },
  {
    id: "events",
    title: "Planning the Events",
    shortTitle: "Events",
    summary: "Build the service schedule one gathering at a time.",
    prompt: "Which service moments should be included?",
    step: 2,
    icon: CalendarDays,
    options: ["Traditional funeral", "Celebration of life", "Memorial service", "Graveside gathering", "Private family moment"],
    helperFields: ["Date and time", "Location", "Livestream", "Guest count"],
  },
  {
    id: "people_helping",
    title: "People Helping with the Service",
    shortTitle: "People",
    summary: "Name the people who should lead, speak, carry, sing, or be recognized.",
    prompt: "Who should Monarch account for in the ceremony plan?",
    step: 2,
    icon: Users,
    options: ["Clergy", "Celebrant", "Family speaker", "Pallbearers", "Musicians"],
    helperFields: ["Eulogy", "Readings", "Honorary pallbearers", "Bagpiper or soloist"],
  },
  {
    id: "personal_touches",
    title: "Making It Special",
    shortTitle: "Personal",
    summary: "Add the details that make the service feel specific to their life.",
    prompt: "Which personal touches should be explored?",
    step: 3,
    icon: Sparkles,
    options: ["Music", "Flowers", "Photo tribute", "Memory table", "Military honors", "Color theme"],
    helperFields: ["Favorite songs", "Florist", "Photos and video", "Special tributes"],
  },
  {
    id: "printed_online",
    title: "Printed Keepsakes and Online Memorials",
    shortTitle: "Printed",
    summary: "Create the service folder, guestbook, obituary, cards, and memorial hub.",
    prompt: "Which printed or digital items should be prepared?",
    step: 3,
    icon: FileText,
    options: ["Service program", "Guest book", "Memorial cards", "Thank you notes", "Obituary", "Online memorial"],
    helperFields: ["Cover photo", "Poem or prayer", "Newspaper", "QR service folder"],
    productKinds: ["service_folder", "guestbook"],
  },
  {
    id: "transportation",
    title: "Transportation for the Day",
    shortTitle: "Transport",
    summary: "Plan the movement between service locations without adding stress.",
    prompt: "Which transportation support should Monarch coordinate?",
    step: 3,
    icon: Truck,
    options: ["Hearse", "Family limousine", "Chauffeured car", "Procession escort", "Parking support"],
    helperFields: ["Pickup location", "Cemetery route", "Family party size", "Escort request"],
  },
  {
    id: "cemetery_resting_place",
    title: "Cemetery and Final Resting Place",
    shortTitle: "Resting Place",
    summary: "Capture cemetery, grave, niche, marker, tent, chairs, or scattering wishes.",
    prompt: "What should Monarch know about the final resting place?",
    step: 3,
    icon: MapPin,
    options: ["Choose a cemetery", "Existing family space", "Mausoleum crypt", "Columbarium niche", "Scattering ashes", "Headstone or marker"],
    helperFields: ["Cemetery name", "Plot or niche", "Marker wording", "Tent and chairs"],
  },
  {
    id: "reception",
    title: "The Reception",
    shortTitle: "Reception",
    summary: "Plan the gathering afterward with the same care as the service.",
    prompt: "What kind of gathering should follow the service?",
    step: 3,
    icon: Gift,
    options: ["Funeral home reception", "Restaurant", "Banquet hall", "Family home", "Casual potluck"],
    helperFields: ["Menu style", "Beverages", "Guest count", "Host contact"],
  },
  {
    id: "paperwork_payment",
    title: "Important Paperwork and Details",
    shortTitle: "Paperwork",
    summary: "Collect the practical information the director will verify.",
    prompt: "Which paperwork details should be prepared for director review?",
    step: 4,
    icon: ShieldCheck,
    options: ["Death certificates", "Charity donations", "Life insurance", "Pre-planned trust", "Payment conversation", "Family funding page"],
    helperFields: ["Certificate count", "Donation charity", "Policy contact", "Purchaser details"],
  },
];

function createInitialAnswers() {
  return categories.reduce<Record<string, CategoryAnswer>>((answers, category) => {
    answers[category.id] = { selected: [], notes: "", needsHelp: false };
    return answers;
  }, {});
}

export default function FamilyArrangementStudioScreen() {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 760;
  const scrollRef = useRef<ScrollView>(null);
  const mountedRef = useRef(true);
  const loadVersionRef = useRef(0);
  const saveVersionRef = useRef(0);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const [starter, setStarter] = useState<StarterForm>(emptyStarter);
  const [answers, setAnswers] = useState<Record<string, CategoryAnswer>>(createInitialAnswers);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [guideVisible, setGuideVisible] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [editingStarter, setEditingStarter] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [draftLoadError, setDraftLoadError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [touchedFields, setTouchedFields] = useState<Set<FamilyStarterField>>(new Set());
  const [submissionStatus, setSubmissionStatus] = useState<FamilySubmissionStatus>("idle");
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [draftChangedAfterSubmission, setDraftChangedAfterSubmission] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadDraft = useCallback(async () => {
    const version = ++loadVersionRef.current;
    setHydrated(false);
    setDraftLoadError(null);
    try {
      const [rawDraft, rawSubmission] = await Promise.all([
        AsyncStorage.getItem(FAMILY_DRAFT_STORAGE_KEY),
        AsyncStorage.getItem(FAMILY_SUBMISSION_STORAGE_KEY),
      ]);
      if (version !== loadVersionRef.current) return;

      if (rawDraft) {
        const parsed = JSON.parse(rawDraft) as {
          starter?: StarterForm;
          answers?: Record<string, CategoryAnswer>;
          activeCategoryIndex?: number;
          reviewMode?: boolean;
        };
        if (parsed.starter) setStarter({ ...emptyStarter, ...parsed.starter });
        if (parsed.answers) setAnswers({ ...createInitialAnswers(), ...parsed.answers });
        if (typeof parsed.activeCategoryIndex === "number") {
          setActiveCategoryIndex(Math.min(Math.max(parsed.activeCategoryIndex, 0), categories.length - 1));
        }
        if (typeof parsed.reviewMode === "boolean") setReviewMode(parsed.reviewMode);
      }

      if (rawSubmission) {
        const submission = parseFamilySubmissionRecord(rawSubmission);
        if (submission) setSubmissionStatus(submission.status);
      }
      setSaveStatus(rawDraft ? "saved" : "idle");
    } catch {
      setDraftLoadError(
        "We couldn’t safely restore the saved arrangement. You can retry or start a fresh plan without affecting anything already sent to Monarch.",
      );
    } finally {
      if (version === loadVersionRef.current) setHydrated(true);
    }
  }, []);

  useEffect(() => {
    loadDraft();
    return () => {
      loadVersionRef.current += 1;
    };
  }, [loadDraft]);

  const serializedDraft = useMemo(
    () => JSON.stringify({ starter, answers, activeCategoryIndex, reviewMode }),
    [activeCategoryIndex, answers, reviewMode, starter],
  );

  useEffect(() => {
    if (!hydrated || draftLoadError) return;
    const version = ++saveVersionRef.current;
    setSaveStatus("saving");
    const timer = setTimeout(() => {
      const queuedSave = saveQueueRef.current
        .catch(() => undefined)
        .then(() => AsyncStorage.setItem(FAMILY_DRAFT_STORAGE_KEY, serializedDraft));
      saveQueueRef.current = queuedSave;
      queuedSave
        .then(() => {
          if (mountedRef.current && version === saveVersionRef.current) setSaveStatus("saved");
        })
        .catch(() => {
          if (mountedRef.current && version === saveVersionRef.current) setSaveStatus("error");
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [draftLoadError, hydrated, serializedDraft]);

  const retrySaveDraft = useCallback(async () => {
    setSaveStatus("saving");
    try {
      await AsyncStorage.setItem(FAMILY_DRAFT_STORAGE_KEY, serializedDraft);
      if (!mountedRef.current) return;
      setSaveStatus("saved");
      AccessibilityInfo.announceForAccessibility("Arrangement progress saved on this device.");
    } catch {
      if (!mountedRef.current) return;
      setSaveStatus("error");
      AccessibilityInfo.announceForAccessibility("Arrangement progress could not be saved.");
    }
  }, [serializedDraft]);

  const startFreshDraft = useCallback(async () => {
    await AsyncStorage.multiRemove([FAMILY_DRAFT_STORAGE_KEY, FAMILY_SUBMISSION_STORAGE_KEY]).catch(() => undefined);
    if (!mountedRef.current) return;
    setStarter(emptyStarter);
    setAnswers(createInitialAnswers());
    setActiveCategoryIndex(0);
    setReviewMode(false);
    setEditingStarter(false);
    setSubmissionStatus("idle");
    setSubmissionError(null);
    setDraftChangedAfterSubmission(false);
    setTouchedFields(new Set());
    setDraftLoadError(null);
    setHydrated(true);
  }, []);

  const starterErrors = useMemo(() => validateFamilyStarter(starter), [starter]);
  const starterComplete = familyStarterIsComplete(starterErrors);
  const visibleStarterErrors = useMemo(() => {
    const visible: FamilyStarterErrors = {};
    const contactTouched = touchedFields.has("phone") || touchedFields.has("email");
    (Object.entries(starterErrors) as [FamilyStarterField, string][]).forEach(([field, message]) => {
      if (touchedFields.has(field) || ((field === "phone" || field === "email") && contactTouched)) {
        visible[field] = message;
      }
    });
    return visible;
  }, [starterErrors, touchedFields]);
  const activeCategory = categories[activeCategoryIndex] ?? categories[0]!;
  const activeAnswer = answers[activeCategory.id] ?? { selected: [], notes: "", needsHelp: false };
  const completedIds = useMemo(
    () =>
      categories
        .filter((category) => {
          const answer = answers[category.id];
          return Boolean(answer?.selected.length || answer?.notes.trim() || answer?.needsHelp);
        })
        .map((category) => category.id),
    [answers],
  );
  const completedCount = completedIds.length;
  const activeStepIndex = !starterComplete ? 0 : reviewMode ? 3 : activeCategory.step - 1;
  const lovedOneName = [starter.lovedFirstName.trim(), starter.lovedLastName.trim()].filter(Boolean).join(" ");
  const remainingCount = categories.length - completedCount;

  useEffect(() => {
    if (!starterComplete) return;
    const frame = requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [activeCategoryIndex, reviewMode, starterComplete]);

  const casketProducts = useMemo(() => getProductsByCategory("casket"), []);
  const urnProducts = useMemo(() => getProductsByCategory("urn"), []);
  const keepsakeProducts = useMemo(() => getProductsByCategory("keepsake"), []);
  const folderProducts = useMemo(() => getProductsByCategory("service_folder"), []);
  const productGroups: Record<ArrangementProduct["category"], ArrangementProduct[]> = {
    casket: casketProducts,
    urn: urnProducts,
    keepsake: keepsakeProducts,
    service_folder: folderProducts,
    guestbook: [],
  };

  const invalidatePriorSubmission = () => {
    if (submissionStatus === "submitted" || submissionStatus === "queued_offline") {
      setSubmissionStatus("idle");
      setDraftChangedAfterSubmission(true);
      AsyncStorage.removeItem(FAMILY_SUBMISSION_STORAGE_KEY).catch(() => undefined);
    }
    setSubmissionError(null);
  };

  const updateStarter = (key: keyof StarterForm, value: string) => {
    invalidatePriorSubmission();
    setStarter((current) => ({ ...current, [key]: value }));
  };

  const updateActiveAnswer = (patch: Partial<CategoryAnswer>) => {
    invalidatePriorSubmission();
    setAnswers((current) => ({
      ...current,
      [activeCategory.id]: {
        selected: patch.selected ?? current[activeCategory.id]?.selected ?? [],
        notes: patch.notes ?? current[activeCategory.id]?.notes ?? "",
        needsHelp: patch.needsHelp ?? current[activeCategory.id]?.needsHelp ?? false,
      },
    }));
  };

  const toggleOption = (option: string) => {
    Haptics.selectionAsync().catch(() => undefined);
    const selected = activeAnswer.selected.includes(option)
      ? activeAnswer.selected.filter((item) => item !== option)
      : [...activeAnswer.selected, option];
    updateActiveAnswer({ selected });
  };

  const moveToCategory = (index: number) => {
    Haptics.selectionAsync().catch(() => undefined);
    setReviewMode(false);
    setActiveCategoryIndex(Math.min(Math.max(index, 0), categories.length - 1));
  };

  const goNext = () => {
    Haptics.selectionAsync().catch(() => undefined);
    if (activeCategoryIndex >= categories.length - 1) {
      setReviewMode(true);
      return;
    }
    setActiveCategoryIndex((index) => index + 1);
  };

  const sendToDirector = async () => {
    const online = isOnline();
    const blockReason = getSubmissionBlockReason({
      errors: starterErrors,
      online,
      status: submissionStatus,
    });

    if (blockReason === "validation") {
      setTouchedFields(new Set(Object.keys(starter) as FamilyStarterField[]));
      const message = submissionBlockMessage(blockReason);
      setSubmissionError(message);
      AccessibilityInfo.announceForAccessibility(message);
      return;
    }

    if (blockReason === "submitting" || blockReason === "already_submitted") {
      const message = submissionBlockMessage(blockReason);
      setSubmissionError(message);
      AccessibilityInfo.announceForAccessibility(message);
      return;
    }

    const idempotencyKey = createFamilySubmissionKey(starter, answers);
    if (blockReason === "offline") {
      const queuedRecord: FamilySubmissionRecord = {
        idempotencyKey,
        status: "queued_offline",
        queuedAt: new Date().toISOString(),
      };
      try {
        await AsyncStorage.setItem(FAMILY_SUBMISSION_STORAGE_KEY, JSON.stringify(queuedRecord));
        if (!mountedRef.current) return;
        setSubmissionStatus("queued_offline");
        setSubmissionError(null);
        setDraftChangedAfterSubmission(false);
        AccessibilityInfo.announceForAccessibility(submissionBlockMessage("offline"));
      } catch {
        if (!mountedRef.current) return;
        const message = "Your progress is still visible, but this request could not be queued. Retry before closing the app.";
        setSubmissionStatus("error");
        setSubmissionError(message);
        AccessibilityInfo.announceForAccessibility(message);
      }
      return;
    }

    setSubmissionStatus("submitting");
    setSubmissionError(null);
    try {
      const existingRaw = await AsyncStorage.getItem(FAMILY_SUBMISSION_STORAGE_KEY);
      if (!mountedRef.current) return;
      if (existingRaw) {
        const existing = parseFamilySubmissionRecord(existingRaw);
        if (existing?.status === "submitted" && existing.idempotencyKey === idempotencyKey) {
          setSubmissionStatus("submitted");
          AccessibilityInfo.announceForAccessibility(
            "This arrangement request was already sent to Monarch. No duplicate was created.",
          );
          return;
        }
      }

      const submittedRecord: FamilySubmissionRecord = {
        idempotencyKey,
        status: "submitted",
        submittedAt: new Date().toISOString(),
      };
      await AsyncStorage.setItem(FAMILY_SUBMISSION_STORAGE_KEY, JSON.stringify(submittedRecord));
      if (!mountedRef.current) return;
      setSubmissionStatus("submitted");
      setDraftChangedAfterSubmission(false);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success).catch(() => undefined);
      AccessibilityInfo.announceForAccessibility(
        "Arrangement request sent to Monarch for director review.",
      );
    } catch {
      if (!mountedRef.current) return;
      const message = "We couldn’t send the arrangement request. Your answers remain saved; retry when you’re ready.";
      setSubmissionStatus("error");
      setSubmissionError(message);
      AccessibilityInfo.announceForAccessibility(message);
    }
  };

  const saveLabel =
    saveStatus === "saving"
      ? "Saving privately…"
      : saveStatus === "error"
        ? "Progress not saved"
        : saveStatus === "saved"
          ? "Private progress saved"
          : "Private draft on this device";

  const phone = (
    <View style={[styles.phoneSurface, isDesktop ? styles.phoneSurfaceDesktop : null]}>
      <View style={styles.topBar}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={() => router.back()}
          style={styles.iconButton}
        >
          <ArrowLeft color={semantic.text.inverse} size={24} strokeWidth={2.2} />
        </Pressable>
        <Text style={styles.topTitle}>Arrangement Studio</Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Show planning guide"
          onPress={() => setGuideVisible(true)}
          style={styles.iconButton}
        >
          <MessageCircle color={semantic.text.inverse} size={24} strokeWidth={2.1} />
        </Pressable>
      </View>

      <ScrollView
        ref={scrollRef}
        style={styles.scroll}
        contentContainerStyle={[styles.scrollContent, isDesktop ? styles.scrollContentDesktop : null]}
        showsVerticalScrollIndicator={false}
      >
        {!hydrated ? (
          <LoadingState
            label="Restoring your private arrangement"
            description="Your answers stay on this device while the draft opens."
          />
        ) : draftLoadError ? (
          <View style={styles.recoveryStack}>
            <ErrorState
              title="Your saved arrangement needs attention"
              description={draftLoadError}
              action={{ label: "Retry restore", onPress: loadDraft }}
            />
            <StudioButton label="Start a fresh plan" variant="secondary" onPress={startFreshDraft} />
          </View>
        ) : (
          <>
            <StepProgress activeStepIndex={activeStepIndex} />

            {guideVisible ? (
              <AiGuide
                starterComplete={starterComplete}
                reviewMode={reviewMode}
                activeCategory={activeCategory}
                onDismiss={() => setGuideVisible(false)}
              />
            ) : null}

            {!starterComplete || editingStarter ? (
              <StarterGate
                starter={starter}
                starterComplete={starterComplete}
                errors={visibleStarterErrors}
                updateStarter={updateStarter}
                onFieldBlur={(field) =>
                  setTouchedFields((current) => new Set(current).add(field))
                }
                completedCount={completedCount}
                saveLabel={saveLabel}
                saveStatus={saveStatus}
                onRetrySave={retrySaveDraft}
                onContinue={() => setEditingStarter(false)}
              />
            ) : reviewMode ? (
              <ReviewPanel
                lovedOneName={lovedOneName}
                starter={starter}
                answers={answers}
                completedCount={completedCount}
                remainingCount={remainingCount}
                submissionStatus={submissionStatus}
                submissionError={submissionError}
                draftChangedAfterSubmission={draftChangedAfterSubmission}
                onEditStarter={() => {
                  setReviewMode(false);
                  setEditingStarter(true);
                }}
                onEditCategory={moveToCategory}
                onSubmit={sendToDirector}
                onOpenConfirmation={() => router.push(arrangementRoutes.confirmation as never)}
              />
            ) : (
              <>
                <PlanningSummary
                  lovedOneName={lovedOneName}
                  starter={starter}
                  completedCount={completedCount}
                  remainingCount={remainingCount}
                  onEdit={() => setEditingStarter(true)}
                />
                <CategoryNavigator
                  activeCategoryIndex={activeCategoryIndex}
                  completedIds={completedIds}
                  onSelect={moveToCategory}
                />
                <CategoryWorkbench
                  category={activeCategory}
                  answer={activeAnswer}
                  categoryIndex={activeCategoryIndex}
                  productGroups={productGroups}
                  onToggleOption={toggleOption}
                  onChangeNotes={(notes) => updateActiveAnswer({ notes })}
                  onToggleHelp={() => updateActiveAnswer({ needsHelp: !activeAnswer.needsHelp })}
                  onPrevious={() => moveToCategory(activeCategoryIndex - 1)}
                  onNext={goNext}
                  onReview={() => setReviewMode(true)}
                />
                <ArrangementOverview
                  answers={answers}
                  completedCount={completedCount}
                  remainingCount={remainingCount}
                  onReview={() => setReviewMode(true)}
                />
                <DigitalFolderPreview unlocked={completedIds.includes("printed_online")} />
              </>
            )}

            <View style={styles.directorNotice}>
              <ShieldCheck color={semantic.action.primary} size={28} strokeWidth={1.9} />
              <View style={styles.noticeCopy}>
                <Text style={styles.noticeTitle}>Director review required</Text>
                <Text style={styles.noticeBody}>Nothing is final until Monarch confirms details, availability, and paperwork.</Text>
              </View>
            </View>
          </>
        )}
      </ScrollView>
      {hydrated && !draftLoadError && starterComplete ? (
        <BlueprintDock
          activeCategoryIndex={activeCategoryIndex}
          completedIds={completedIds}
          remainingCount={remainingCount}
          reviewMode={reviewMode}
          onSelect={moveToCategory}
          onReview={() => setReviewMode(true)}
        />
      ) : null}
    </View>
  );

  return (
    <AppShell role="family" accessibilityLabel="Family arrangement studio">
      <LinearGradient colors={[semantic.surface.canvas, semantic.surface.canvasAlt]} style={styles.stage}>
        {isDesktop ? <View style={styles.desktopMat}>{phone}</View> : phone}
      </LinearGradient>
    </AppShell>
  );
}

function StepProgress({ activeStepIndex }: { activeStepIndex: number }) {
  return (
    <View style={styles.progressWrap}>
      <View style={styles.progressLine} />
      {progressSteps.map((step, index) => {
        const active = index === activeStepIndex;
        const done = index < activeStepIndex;
        return (
          <View key={step.number} style={styles.progressItem}>
            <View
              style={[
                styles.progressCircle,
                active ? styles.progressCircleActive : null,
                done ? styles.progressCircleDone : null,
              ]}
            >
              {done ? (
                <Check color={semantic.text.inverse} size={16} strokeWidth={2.2} />
              ) : (
                <Text style={[styles.progressNumber, active ? styles.progressNumberActive : null]}>
                  {step.number}
                </Text>
              )}
            </View>
            <Text style={[styles.progressLabel, active ? styles.progressLabelActive : null]}>{step.label}</Text>
          </View>
        );
      })}
    </View>
  );
}

function AiGuide({
  starterComplete,
  reviewMode,
  activeCategory,
  onDismiss,
}: {
  starterComplete: boolean;
  reviewMode: boolean;
  activeCategory: StudioCategory;
  onDismiss: () => void;
}) {
  const title = !starterComplete
    ? "Let's start with the essentials."
    : reviewMode
      ? "Review everything before sending."
      : activeCategory.title;
  const body = !starterComplete
    ? "Enter the family contact and loved-one details first. Then I'll unlock the arrangement categories."
    : reviewMode
      ? "The director will review anything marked uncertain. You can still go back to any section."
      : activeCategory.prompt;
  const compact = starterComplete;

  return (
    <View style={[styles.aiGuide, compact ? styles.aiGuideCompact : null]}>
      <View style={[styles.aiAvatar, compact ? styles.aiAvatarCompact : null]}>
        <Bot color={semantic.status.info.foreground} size={compact ? 24 : 35} strokeWidth={2.1} />
      </View>
      <View style={styles.aiCopy}>
        <Text style={styles.aiTitle}>{title}</Text>
        <Text style={styles.aiBody}>{body}</Text>
        {!compact ? <Text style={styles.aiHint}>Your progress is saved privately on this device.</Text> : null}
      </View>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Dismiss AI guide"
        onPress={onDismiss}
        style={styles.closeButton}
      >
        <X color={semantic.text.secondary} size={18} strokeWidth={2} />
      </Pressable>
    </View>
  );
}

function StarterGate({
  starter,
  starterComplete,
  errors,
  updateStarter,
  onFieldBlur,
  completedCount,
  saveLabel,
  saveStatus,
  onRetrySave,
  onContinue,
}: {
  starter: StarterForm;
  starterComplete: boolean;
  errors: FamilyStarterErrors;
  updateStarter: (key: keyof StarterForm, value: string) => void;
  onFieldBlur: (field: FamilyStarterField) => void;
  completedCount: number;
  saveLabel: string;
  saveStatus: "idle" | "saving" | "saved" | "error";
  onRetrySave: () => void;
  onContinue: () => void;
}) {
  return (
    <View style={styles.starterPanel}>
      <View style={styles.starterHeader}>
        <Text style={styles.sectionTitle}>Planning For</Text>
        <View style={styles.saveStatusRow}>
          <Text
            accessibilityLiveRegion={saveStatus === "error" ? "assertive" : "polite"}
            style={[styles.privateSave, saveStatus === "error" ? styles.privateSaveError : null]}
          >
            {saveLabel}
          </Text>
          {saveStatus === "error" ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Retry saving arrangement progress"
              onPress={onRetrySave}
              style={styles.saveRetryButton}
            >
              <Text style={styles.saveRetryText}>Retry</Text>
            </Pressable>
          ) : null}
        </View>
      </View>
      <View style={styles.personCard}>
        <View style={styles.emptyPortrait}>
          <Plus color={semantic.action.primary} size={24} strokeWidth={2} />
        </View>
        <View style={styles.personCopy}>
          <Text style={styles.personName}>Memorial Ledger</Text>
          <Text style={styles.personMeta}>Add the loved one and family contact to open the arrangement workshop.</Text>
        </View>
      </View>

      <View style={styles.formGrid}>
        <Field
          label="Loved one first name"
          value={starter.lovedFirstName}
          placeholder="First name"
          required
          maxLength={100}
          error={errors.lovedFirstName}
          onBlur={() => onFieldBlur("lovedFirstName")}
          onChangeText={(value) => updateStarter("lovedFirstName", value)}
        />
        <Field
          label="Loved one last name"
          value={starter.lovedLastName}
          placeholder="Last name"
          required
          maxLength={100}
          error={errors.lovedLastName}
          onBlur={() => onFieldBlur("lovedLastName")}
          onChangeText={(value) => updateStarter("lovedLastName", value)}
        />
        <Field
          label="Date of birth"
          value={starter.dateOfBirth}
          placeholder="MM/DD/YYYY"
          maxLength={10}
          error={errors.dateOfBirth}
          onBlur={() => onFieldBlur("dateOfBirth")}
          onChangeText={(value) => updateStarter("dateOfBirth", value)}
        />
        <Field
          label="Date of passing"
          value={starter.dateOfPassing}
          placeholder="MM/DD/YYYY or unsure"
          maxLength={10}
          error={errors.dateOfPassing}
          onBlur={() => onFieldBlur("dateOfPassing")}
          onChangeText={(value) => updateStarter("dateOfPassing", value)}
        />
        <Field
          label="Your name"
          value={starter.plannerName}
          placeholder="Family contact"
          required
          maxLength={100}
          error={errors.plannerName}
          onBlur={() => onFieldBlur("plannerName")}
          onChangeText={(value) => updateStarter("plannerName", value)}
        />
        <Field
          label="Relationship"
          value={starter.relationship}
          placeholder="Daughter, spouse, friend..."
          required
          maxLength={120}
          error={errors.relationship}
          onBlur={() => onFieldBlur("relationship")}
          onChangeText={(value) => updateStarter("relationship", value)}
        />
        <Field
          label="Phone"
          value={starter.phone}
          placeholder="Best number"
          keyboardType="phone-pad"
          textContentType="telephoneNumber"
          maxLength={32}
          error={errors.phone}
          accessibilityHint="Enter a phone number or provide an email address below"
          onBlur={() => onFieldBlur("phone")}
          onChangeText={(value) => updateStarter("phone", value)}
        />
        <Field
          label="Email"
          value={starter.email}
          placeholder="Best email"
          keyboardType="email-address"
          textContentType="emailAddress"
          autoCapitalize="none"
          autoCorrect={false}
          maxLength={254}
          error={errors.email}
          accessibilityHint="Enter an email address or provide a phone number above"
          onBlur={() => onFieldBlur("email")}
          onChangeText={(value) => updateStarter("email", value)}
        />
      </View>

      <View style={styles.unlockPanel}>
        <View style={styles.unlockCopy}>
          <Text style={styles.unlockTitle}>{starterComplete ? "Categories unlocked" : "Selections are locked"}</Text>
          <Text style={styles.unlockBody}>
            {starterComplete
              ? "You can now move through each arrangement category."
              : "Complete the required fields and either phone or email to continue."}
          </Text>
        </View>
        <View style={[styles.lockBadge, starterComplete ? styles.lockBadgeReady : null]}>
          <Text style={[styles.lockBadgeText, starterComplete ? styles.lockBadgeTextReady : null]}>
            {starterComplete ? `${completedCount}/11` : "Locked"}
          </Text>
        </View>
      </View>
      {starterComplete ? (
        <StudioButton label="Continue to arrangement" onPress={onContinue} />
      ) : null}
    </View>
  );
}

function PlanningSummary({
  lovedOneName,
  starter,
  completedCount,
  remainingCount,
  onEdit,
}: {
  lovedOneName: string;
  starter: StarterForm;
  completedCount: number;
  remainingCount: number;
  onEdit: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Edit family details for ${lovedOneName}`}
      accessibilityHint="Returns to loved-one and family contact fields"
      onPress={onEdit}
      style={styles.personCard}
    >
      <View style={styles.initialsPortrait}>
        <Text style={styles.initialsText}>{initialsFor(lovedOneName)}</Text>
      </View>
      <View style={styles.personCopy}>
        <Text style={styles.personName}>{lovedOneName}</Text>
        <Text style={styles.personMeta}>
          {starter.plannerName} · {starter.relationship}
        </Text>
        <Text style={styles.personProgress}>{completedCount} complete · {remainingCount} remaining</Text>
      </View>
      <ChevronRight color={semantic.text.secondary} size={24} strokeWidth={1.9} />
    </Pressable>
  );
}

function CategoryNavigator({
  activeCategoryIndex,
  completedIds,
  onSelect,
}: {
  activeCategoryIndex: number;
  completedIds: string[];
  onSelect: (index: number) => void;
}) {
  return (
    <View style={styles.categoryRailBlock}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Arrangement Categories</Text>
        <Text style={styles.textLink}>{activeCategoryIndex + 1}/11</Text>
      </View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryRail}>
        {categories.map((category, index) => {
          const Icon = category.icon;
          const active = index === activeCategoryIndex;
          const complete = completedIds.includes(category.id);
          return (
            <Pressable
              key={category.id}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              onPress={() => onSelect(index)}
              style={[
                styles.categoryPill,
                active ? styles.categoryPillActive : null,
                complete ? styles.categoryPillComplete : null,
              ]}
            >
              <View style={[styles.categoryIcon, active ? styles.categoryIconActive : null]}>
                {complete ? (
                  <Check color={active ? semantic.text.inverse : semantic.action.primary} size={14} strokeWidth={2.2} />
                ) : (
                  <Icon color={active ? semantic.text.inverse : semantic.action.primary} size={14} strokeWidth={1.8} />
                )}
              </View>
              <Text style={[styles.categoryPillText, active ? styles.categoryPillTextActive : null]}>
                {category.shortTitle}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

function CategoryWorkbench({
  category,
  answer,
  categoryIndex,
  productGroups,
  onToggleOption,
  onChangeNotes,
  onToggleHelp,
  onPrevious,
  onNext,
  onReview,
}: {
  category: StudioCategory;
  answer: CategoryAnswer;
  categoryIndex: number;
  productGroups: Record<ArrangementProduct["category"], ArrangementProduct[]>;
  onToggleOption: (option: string) => void;
  onChangeNotes: (notes: string) => void;
  onToggleHelp: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onReview: () => void;
}) {
  const Icon = category.icon;
  return (
    <View style={styles.workbench}>
      <View style={styles.workbenchTop}>
        <View style={styles.workbenchIcon}>
          <Icon color={semantic.action.primary} size={24} strokeWidth={1.8} />
        </View>
        <View style={styles.workbenchCopy}>
          <Text style={styles.workbenchEyebrow}>Section {categoryIndex + 1} of 11</Text>
          <Text style={styles.workbenchTitle}>{category.title}</Text>
          <Text style={styles.workbenchSummary}>{category.summary}</Text>
        </View>
      </View>

      <View style={styles.promptBox}>
        <Text style={styles.promptText}>{category.prompt}</Text>
      </View>

      <View style={styles.draftingNote}>
        <PenLine color={semantic.accent.foreground} size={16} strokeWidth={1.9} />
        <Text style={styles.draftingNoteText}>
          Choose what feels right today. Monarch will confirm details, availability, and timing before anything is final.
        </Text>
      </View>

      <View style={styles.optionGrid}>
        {category.options.map((option) => {
          const selected = answer.selected.includes(option);
          return (
            <Pressable
              key={option}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: selected }}
              onPress={() => onToggleOption(option)}
              style={[styles.optionChip, selected ? styles.optionChipSelected : null]}
            >
              <View style={[styles.optionCheck, selected ? styles.optionCheckSelected : null]}>
                {selected ? <Check color={semantic.text.inverse} size={13} strokeWidth={2.3} /> : null}
              </View>
              <Text style={[styles.optionText, selected ? styles.optionTextSelected : null]}>{option}</Text>
            </Pressable>
          );
        })}
      </View>

      {category.productKinds?.length ? (
        <View style={styles.productSection}>
          <Text style={styles.subsectionTitle}>Suggested selections</Text>
          {category.productKinds.map((kind) => (
            <ProductStrip
              key={kind}
              products={productGroups[kind]}
              selected={answer.selected}
              onToggle={onToggleOption}
            />
          ))}
        </View>
      ) : null}

      <View style={styles.helperFields}>
        {category.helperFields.map((field) => (
          <View key={field} style={styles.helperField}>
            <PenLine color={semantic.accent.foreground} size={14} strokeWidth={1.8} />
            <Text style={styles.helperFieldText}>{field}</Text>
          </View>
        ))}
      </View>

      <Field
        label="Notes for Monarch"
        value={answer.notes}
        placeholder="Add anything the director should know..."
        multiline
        maxLength={1000}
        hint={`${answer.notes.length}/1000 characters`}
        onChangeText={onChangeNotes}
      />

      <Pressable
        accessibilityRole="checkbox"
        accessibilityState={{ checked: answer.needsHelp }}
        onPress={onToggleHelp}
        style={[styles.helpToggle, answer.needsHelp ? styles.helpToggleActive : null]}
      >
        <CircleHelp color={answer.needsHelp ? semantic.text.inverse : semantic.text.muted} size={20} strokeWidth={1.8} />
        <View style={styles.helpToggleCopy}>
          <Text style={[styles.helpToggleTitle, answer.needsHelp ? styles.helpToggleTitleActive : null]}>
            Ask Bill Savino to review this section
          </Text>
          <Text style={[styles.helpToggleBody, answer.needsHelp ? styles.helpToggleBodyActive : null]}>
            Leave this open for Monarch instead of forcing an answer.
          </Text>
        </View>
      </Pressable>

      <View style={styles.actionRow}>
        <StudioButton label="Back" variant="secondary" disabled={categoryIndex === 0} onPress={onPrevious} />
        <StudioButton label={categoryIndex === categories.length - 1 ? "Review" : "Next section"} onPress={onNext} />
      </View>
      <Pressable accessibilityRole="button" onPress={onReview} style={styles.reviewLinkButton}>
        <Text style={styles.reviewLinkText}>Review arrangement so far</Text>
      </Pressable>
    </View>
  );
}

function BlueprintDock({
  activeCategoryIndex,
  completedIds,
  remainingCount,
  reviewMode,
  onSelect,
  onReview,
}: {
  activeCategoryIndex: number;
  completedIds: string[];
  remainingCount: number;
  reviewMode: boolean;
  onSelect: (index: number) => void;
  onReview: () => void;
}) {
  return (
    <View style={styles.blueprintDock}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.blueprintTrack}>
        {categories.map((category, index) => {
          const complete = completedIds.includes(category.id);
          const active = !reviewMode && index === activeCategoryIndex;
          return (
            <Pressable
              key={category.id}
              accessibilityRole="button"
              accessibilityLabel={`${category.shortTitle} section`}
              accessibilityState={{ selected: active }}
              onPress={() => onSelect(index)}
              style={styles.milestoneHit}
            >
              <View
                style={[
                  styles.milestoneDot,
                  complete ? styles.milestoneDotComplete : null,
                  active ? styles.milestoneDotActive : null,
                ]}
              >
                {complete ? <Check color={active ? semantic.text.inverse : semantic.accent.strong} size={11} strokeWidth={2.2} /> : null}
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
      <Pressable accessibilityRole="button" onPress={onReview} style={styles.blueprintReviewButton}>
        <Text style={styles.blueprintReviewText}>{reviewMode ? "Reviewing" : "Review"}</Text>
        <Text style={styles.blueprintStatusText}>{remainingCount} open</Text>
      </Pressable>
    </View>
  );
}

function ProductStrip({
  products,
  selected,
  onToggle,
}: {
  products: ArrangementProduct[];
  selected: string[];
  onToggle: (option: string) => void;
}) {
  if (products.length === 0) return null;
  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.productRail}>
      {products.map((product) => {
        const active = selected.includes(product.name);
        return (
          <Pressable
            key={product.id}
            accessibilityRole="checkbox"
            accessibilityState={{ checked: active }}
            onPress={() => onToggle(product.name)}
            style={[styles.productCard, active ? styles.productCardActive : null]}
          >
            <View style={[styles.productSwatch, { backgroundColor: productTone(product.imageTone) }]}>
              {active ? (
                <Check color={semantic.text.inverse} size={22} strokeWidth={2.2} />
              ) : (
                <Plus color={semantic.action.primary} size={22} strokeWidth={1.8} />
              )}
            </View>
            <Text style={styles.productName}>{product.name}</Text>
            <Text style={styles.productBody} numberOfLines={2}>{product.description}</Text>
            <Text style={styles.productMeta}>Director review</Text>
          </Pressable>
        );
      })}
    </ScrollView>
  );
}

function ArrangementOverview({
  answers,
  completedCount,
  remainingCount,
  onReview,
}: {
  answers: Record<string, CategoryAnswer>;
  completedCount: number;
  remainingCount: number;
  onReview: () => void;
}) {
  const selectedItems = categories
    .flatMap((category) =>
      (answers[category.id]?.selected ?? []).slice(0, 2).map((selection) => ({
        category: category.shortTitle,
        selection,
      })),
    )
    .slice(0, 5);

  return (
    <View style={styles.overviewPanel}>
      <View style={styles.sectionHeaderRow}>
        <Text style={styles.sectionTitle}>Arrangement Overview</Text>
        <Text style={styles.textLink}>{completedCount}/11</Text>
      </View>
      <View style={styles.overviewStats}>
        <OverviewStat label="Complete" value={String(completedCount)} />
        <OverviewStat label="Remaining" value={String(remainingCount)} />
        <OverviewStat
          label="Director help"
          value={String(categories.filter((category) => answers[category.id]?.needsHelp).length)}
        />
      </View>
      {selectedItems.length ? (
        <View style={styles.selectionSummary}>
          {selectedItems.map((item) => (
            <View key={`${item.category}-${item.selection}`} style={styles.summaryRow}>
              <Text style={styles.summaryCategory}>{item.category}</Text>
              <Text style={styles.summarySelection}>{item.selection}</Text>
            </View>
          ))}
        </View>
      ) : (
        <Text style={styles.emptySummary}>Selections and notes will appear here as each category is completed.</Text>
      )}
      <StudioButton label="Review wishes" variant="secondary" onPress={onReview} />
    </View>
  );
}

function OverviewStat({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.overviewStat}>
      <Text style={styles.overviewStatValue}>{value}</Text>
      <Text style={styles.overviewStatLabel}>{label}</Text>
    </View>
  );
}

function DigitalFolderPreview({ unlocked }: { unlocked: boolean }) {
  return (
    <View style={[styles.folderCard, !unlocked ? styles.folderCardLocked : null]}>
      <ImageBackground
        source={monarchHeroImage}
        resizeMode="cover"
        style={styles.folderCover}
        imageStyle={styles.folderCoverImage}
      >
        <View style={styles.folderCoverScrim}>
          <Text style={styles.folderCoverText}>In Loving Memory</Text>
        </View>
      </ImageBackground>
      <View style={styles.folderCopy}>
        <Text style={styles.folderTitle}>Digital Service Folder</Text>
        <Text style={styles.folderBody}>
          {unlocked
            ? "Ready to shape with service details, photos, music, and guestbook messages."
            : "Unlock this by completing Printed Keepsakes and Online Memorials."}
        </Text>
        <View style={styles.folderButton}>
          <Text style={styles.folderButtonText}>{unlocked ? "Preview Folder" : "Locked"}</Text>
        </View>
      </View>
      <View style={styles.qrBox}>
        <QRCode value="https://wehonor.app/monarch/service-folder" size={72} />
      </View>
    </View>
  );
}

function ReviewPanel({
  lovedOneName,
  starter,
  answers,
  completedCount,
  remainingCount,
  submissionStatus,
  submissionError,
  draftChangedAfterSubmission,
  onEditStarter,
  onEditCategory,
  onSubmit,
  onOpenConfirmation,
}: {
  lovedOneName: string;
  starter: StarterForm;
  answers: Record<string, CategoryAnswer>;
  completedCount: number;
  remainingCount: number;
  submissionStatus: FamilySubmissionStatus;
  submissionError: string | null;
  draftChangedAfterSubmission: boolean;
  onEditStarter: () => void;
  onEditCategory: (index: number) => void;
  onSubmit: () => void;
  onOpenConfirmation: () => void;
}) {
  const submitLabel =
    submissionStatus === "submitting"
      ? "Sending…"
      : submissionStatus === "queued_offline"
        ? "Retry sending"
        : submissionStatus === "submitted"
          ? "Sent to Monarch"
          : "Send to director";
  const submitDisabled = submissionStatus === "submitting" || submissionStatus === "submitted";

  return (
    <View style={styles.reviewPanel}>
      <View style={styles.reviewHero}>
        <Text style={styles.workbenchEyebrow}>Private family review</Text>
        <Text style={styles.reviewTitle}>{lovedOneName || "Arrangement plan"}</Text>
        <Text style={styles.reviewBody}>
          {starter.plannerName} can send these wishes to Monarch. Any open section remains marked for director help.
        </Text>
      </View>

      <View style={styles.overviewStats}>
        <OverviewStat label="Complete" value={String(completedCount)} />
        <OverviewStat label="Open" value={String(remainingCount)} />
        <OverviewStat
          label="Needs help"
          value={String(categories.filter((category) => answers[category.id]?.needsHelp).length)}
        />
      </View>

      <View style={styles.reviewList}>
        {categories.map((category, index) => {
          const answer = answers[category.id];
          const complete = Boolean(answer?.selected.length || answer?.notes.trim() || answer?.needsHelp);
          return (
            <Pressable
              key={category.id}
              accessibilityRole="button"
              accessibilityLabel={`${category.title}: ${complete ? "complete" : "not started"}`}
              accessibilityHint="Open this arrangement section for editing"
              onPress={() => onEditCategory(index)}
              style={styles.reviewRow}
            >
              <View style={[styles.reviewStatus, complete ? styles.reviewStatusComplete : null]}>
                {complete ? <Check color={semantic.text.inverse} size={14} strokeWidth={2.2} /> : null}
              </View>
              <View style={styles.reviewRowCopy}>
                <Text style={styles.reviewRowTitle}>{category.title}</Text>
                <Text style={styles.reviewRowBody} numberOfLines={2}>
                  {answer?.selected.length
                    ? answer.selected.slice(0, 3).join(", ")
                    : answer?.needsHelp
                      ? "Marked for director help"
                      : "Not started"}
                </Text>
              </View>
              <ChevronRight color={semantic.text.secondary} size={18} strokeWidth={1.8} />
            </Pressable>
          );
        })}
      </View>

      <StudioButton label="Edit family details" variant="secondary" onPress={onEditStarter} />
      <View style={styles.submissionFeedback}>
        {draftChangedAfterSubmission ? (
          <Status label="Changes made since the last send — submit again when ready" tone="warning" />
        ) : null}
        {submissionStatus === "queued_offline" ? (
          <Status label="Saved offline on this device — reconnect and retry" tone="warning" />
        ) : null}
        {submissionStatus === "submitted" ? (
          <Status label="Sent to Monarch for licensed director review" tone="success" />
        ) : null}
        {submissionError ? (
          <ErrorState
            title="The arrangement was not sent"
            description={submissionError}
            action={{ label: "Retry", onPress: onSubmit }}
          />
        ) : null}
      </View>
      <StudioButton label={submitLabel} disabled={submitDisabled} onPress={onSubmit} />
      {submissionStatus === "submitted" ? (
        <StudioButton label="Open confirmation" variant="secondary" onPress={onOpenConfirmation} />
      ) : null}
      <Text style={styles.reviewDisclaimer}>
        This sends wishes for review. Monarch confirms final details before anything is scheduled, printed, published, or finalized.
      </Text>
    </View>
  );
}

function StudioButton({
  label,
  variant = "primary",
  disabled,
  onPress,
}: {
  label: string;
  variant?: "primary" | "secondary";
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[
        styles.studioButton,
        variant === "secondary" ? styles.studioButtonSecondary : null,
        disabled ? styles.studioButtonDisabled : null,
      ]}
    >
      <Text
        style={[
          styles.studioButtonText,
          variant === "secondary" ? styles.studioButtonSecondaryText : null,
          disabled ? styles.studioButtonDisabledText : null,
        ]}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function initialsFor(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "+";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
}

// Material tones are illustration color data for the product preview, not UI tokens.
function productTone(tone: ArrangementProduct["imageTone"]) {
  const colors: Record<ArrangementProduct["imageTone"], string> = {
    oak: "#a66d3c",
    silver: "#a8aaa8",
    white: "#f1eadc",
    pine: "#c7924e",
    marble: "#e3e3df",
    bronze: "#9b6a34",
    gold: "#c9a760",
    ivory: "#eee7dc",
    green: "#6f845f",
  };
  return colors[tone];
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: semantic.surface.canvas,
  },
  stage: {
    flex: 1,
  },
  desktopMat: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.lg,
  },
  phoneSurface: {
    flex: 1,
    width: "100%",
    backgroundColor: semantic.surface.raised,
    overflow: "hidden",
  },
  phoneSurfaceDesktop: {
    maxWidth: 1080,
    maxHeight: 930,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: semantic.overlay.darkSubtle,
    shadowColor: semantic.text.muted,
    shadowOpacity: 0.18,
    shadowRadius: 30,
    shadowOffset: { width: 0, height: 18 },
  },
  topBar: {
    height: 56,
    backgroundColor: semantic.action.primary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
  },
  iconButton: {
    width: 42,
    height: 42,
    alignItems: "center",
    justifyContent: "center",
  },
  topTitle: {
    color: semantic.text.inverse,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 24,
    lineHeight: 28,
  },
  scroll: {
    flex: 1,
    backgroundColor: semantic.surface.raised,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 18,
    gap: 14,
  },
  scrollContentDesktop: {
    paddingHorizontal: 28,
    paddingTop: 18,
    paddingBottom: 22,
    gap: 16,
  },
  progressWrap: {
    minHeight: 70,
    flexDirection: "row",
    justifyContent: "space-between",
    position: "relative",
    paddingHorizontal: 10,
  },
  progressLine: {
    position: "absolute",
    top: 17,
    left: 45,
    right: 45,
    height: 2,
    backgroundColor: semantic.border.muted,
  },
  progressItem: {
    width: 78,
    alignItems: "center",
    gap: 8,
  },
  progressCircle: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 2,
    borderColor: semantic.border.muted,
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    justifyContent: "center",
  },
  progressCircleActive: {
    backgroundColor: semantic.action.primary,
    borderColor: semantic.action.primary,
  },
  progressCircleDone: {
    backgroundColor: semantic.text.secondary,
    borderColor: semantic.text.secondary,
  },
  progressNumber: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 15,
  },
  progressNumberActive: {
    color: semantic.text.inverse,
  },
  progressLabel: {
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 16,
    textAlign: "center",
  },
  progressLabelActive: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
  },
  aiGuide: {
    minHeight: 84,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: semantic.border.muted,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    padding: 11,
    gap: 11,
  },
  aiGuideCompact: {
    minHeight: 68,
    backgroundColor: semantic.surface.raised,
    borderColor: semantic.border.subtle,
    paddingVertical: 9,
  },
  aiAvatar: {
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2,
    borderColor: semantic.status.info.foreground,
    backgroundColor: semantic.status.info.background,
    alignItems: "center",
    justifyContent: "center",
  },
  aiAvatarCompact: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1.5,
  },
  aiCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  aiTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 16,
    lineHeight: 20,
  },
  aiBody: {
    color: semantic.text.primary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 12,
    lineHeight: 17,
  },
  aiHint: {
    color: semantic.text.primary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  closeButton: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
  },
  sectionTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 18,
    lineHeight: 24,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  textLink: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  starterPanel: {
    gap: 13,
  },
  recoveryStack: {
    gap: spacing.md,
  },
  starterHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  saveStatusRow: {
    alignItems: "flex-end",
    gap: 4,
  },
  privateSave: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_700Bold",
    fontSize: 11,
    lineHeight: 15,
  },
  privateSaveError: {
    color: semantic.status.error.foreground,
  },
  saveRetryButton: {
    minHeight: 32,
    minWidth: 48,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: semantic.status.error.foreground,
    paddingHorizontal: 10,
  },
  saveRetryText: {
    color: semantic.status.error.foreground,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
  },
  personCard: {
    minHeight: 86,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    gap: 14,
    shadowColor: semantic.text.primary,
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
  },
  emptyPortrait: {
    width: 58,
    height: 58,
    borderRadius: 29,
    borderWidth: 1,
    borderColor: semantic.border.staff,
    borderStyle: "dashed",
    backgroundColor: semantic.surface.staff,
    alignItems: "center",
    justifyContent: "center",
  },
  initialsPortrait: {
    width: 58,
    height: 58,
    borderRadius: 29,
    backgroundColor: semantic.action.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  initialsText: {
    color: semantic.text.inverse,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 25,
    lineHeight: 27,
  },
  personCopy: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  personName: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 17,
    lineHeight: 22,
  },
  personMeta: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 18,
  },
  personProgress: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  formGrid: {
    gap: 10,
  },
  unlockPanel: {
    borderRadius: 13,
    borderWidth: 1,
    borderColor: semantic.border.staff,
    backgroundColor: semantic.surface.staff,
    flexDirection: "row",
    alignItems: "center",
    padding: 13,
    gap: 12,
  },
  unlockCopy: {
    flex: 1,
    gap: 3,
  },
  unlockTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  unlockBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  lockBadge: {
    borderRadius: 999,
    backgroundColor: semantic.surface.subtle,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  lockBadgeReady: {
    backgroundColor: semantic.action.primary,
  },
  lockBadgeText: {
    color: semantic.text.muted,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  lockBadgeTextReady: {
    color: semantic.text.inverse,
  },
  categoryRailBlock: {
    gap: 10,
  },
  categoryRail: {
    gap: 8,
    paddingRight: 8,
  },
  categoryPill: {
    minWidth: 112,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: semantic.border.muted,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  categoryPillActive: {
    backgroundColor: semantic.action.primary,
    borderColor: semantic.action.primary,
  },
  categoryPillComplete: {
    borderColor: semantic.border.default,
  },
  categoryIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: semantic.surface.staff,
    alignItems: "center",
    justifyContent: "center",
  },
  categoryIconActive: {
    backgroundColor: semantic.overlay.inverseSubtle,
  },
  categoryPillText: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 11,
    lineHeight: 15,
  },
  categoryPillTextActive: {
    color: semantic.text.inverse,
  },
  workbench: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    padding: 12,
    gap: 12,
    shadowColor: semantic.text.primary,
    shadowOpacity: 0.08,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 8 },
  },
  workbenchTop: {
    flexDirection: "row",
    gap: 12,
  },
  workbenchIcon: {
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: semantic.surface.staff,
    alignItems: "center",
    justifyContent: "center",
  },
  workbenchCopy: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  workbenchEyebrow: {
    color: semantic.accent.foreground,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 13,
    textTransform: "uppercase",
    letterSpacing: 0.8,
  },
  workbenchTitle: {
    color: semantic.text.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 24,
    lineHeight: 27,
  },
  workbenchSummary: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  promptBox: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: semantic.border.staff,
    backgroundColor: semantic.surface.staff,
    padding: 10,
  },
  promptText: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 18,
  },
  draftingNote: {
    borderLeftWidth: 2,
    borderLeftColor: semantic.accent.foreground,
    backgroundColor: semantic.surface.raised,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 9,
  },
  draftingNoteText: {
    flex: 1,
    color: semantic.text.secondary,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 11,
    lineHeight: 16,
  },
  optionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  optionChip: {
    minHeight: 40,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    shadowColor: semantic.text.muted,
    shadowOpacity: 0.05,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
  },
  optionChipSelected: {
    backgroundColor: semantic.status.warning.background,
    borderColor: semantic.accent.foreground,
  },
  optionCheck: {
    width: 19,
    height: 19,
    borderRadius: 9.5,
    borderWidth: 1,
    borderColor: semantic.border.muted,
    alignItems: "center",
    justifyContent: "center",
  },
  optionCheckSelected: {
    backgroundColor: semantic.accent.foreground,
    borderColor: semantic.accent.foreground,
  },
  optionText: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 16,
  },
  optionTextSelected: {
    color: semantic.accent.strong,
  },
  productSection: {
    gap: 9,
  },
  subsectionTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 14,
    lineHeight: 19,
  },
  productRail: {
    gap: 10,
    paddingRight: 8,
  },
  productCard: {
    width: 168,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    padding: 10,
    gap: 7,
  },
  productCardActive: {
    borderColor: semantic.action.primary,
    backgroundColor: semantic.surface.staff,
  },
  productSwatch: {
    height: 56,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  productName: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  productBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 10,
    lineHeight: 14,
  },
  productMeta: {
    color: semantic.accent.foreground,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 13,
  },
  helperFields: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 7,
  },
  helperField: {
    borderRadius: 999,
    backgroundColor: semantic.surface.subtle,
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 9,
    paddingVertical: 6,
  },
  helperFieldText: {
    color: semantic.accent.foreground,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 13,
  },
  helpToggle: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 10,
  },
  helpToggleActive: {
    backgroundColor: semantic.surface.accent,
    borderColor: semantic.surface.accent,
  },
  helpToggleCopy: {
    flex: 1,
    gap: 2,
  },
  helpToggleTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 17,
  },
  helpToggleTitleActive: {
    color: semantic.text.inverse,
  },
  helpToggleBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
  helpToggleBodyActive: {
    color: semantic.text.inverse,
  },
  actionRow: {
    flexDirection: "row",
    gap: 10,
  },
  studioButton: {
    flex: 1,
    minHeight: 46,
    borderRadius: 9,
    backgroundColor: semantic.action.primary,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
  },
  studioButtonSecondary: {
    backgroundColor: semantic.surface.raised,
    borderWidth: 1.5,
    borderColor: semantic.action.primary,
  },
  studioButtonDisabled: {
    backgroundColor: semantic.action.disabled,
    borderColor: semantic.action.disabled,
  },
  studioButtonText: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 17,
  },
  studioButtonSecondaryText: {
    color: semantic.action.primary,
  },
  studioButtonDisabledText: {
    color: semantic.text.secondary,
  },
  blueprintDock: {
    minHeight: 66,
    borderTopWidth: 1,
    borderTopColor: semantic.border.subtle,
    backgroundColor: semantic.overlay.inverseStrong,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  blueprintTrack: {
    alignItems: "center",
    gap: 4,
    paddingRight: 6,
  },
  milestoneHit: {
    width: 26,
    height: 34,
    alignItems: "center",
    justifyContent: "center",
  },
  milestoneDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    justifyContent: "center",
  },
  milestoneDotComplete: {
    borderStyle: "solid",
    borderColor: semantic.accent.foreground,
    backgroundColor: semantic.status.warning.background,
  },
  milestoneDotActive: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderStyle: "solid",
    borderColor: semantic.action.primary,
    backgroundColor: semantic.action.primary,
  },
  blueprintReviewButton: {
    width: 94,
    minHeight: 44,
    borderRadius: 9,
    backgroundColor: semantic.action.primary,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  blueprintReviewText: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 15,
  },
  blueprintStatusText: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_700Bold",
    fontSize: 10,
    lineHeight: 13,
  },
  reviewLinkButton: {
    alignItems: "center",
    paddingVertical: 3,
  },
  reviewLinkText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  overviewPanel: {
    borderRadius: 15,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    padding: 14,
    gap: 12,
  },
  overviewStats: {
    flexDirection: "row",
    gap: 8,
  },
  overviewStat: {
    flex: 1,
    borderRadius: 11,
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    paddingVertical: 10,
    gap: 2,
  },
  overviewStatValue: {
    color: semantic.action.primary,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 28,
    lineHeight: 30,
  },
  overviewStatLabel: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 13,
  },
  selectionSummary: {
    gap: 7,
  },
  summaryRow: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    padding: 10,
    gap: 2,
  },
  summaryCategory: {
    color: semantic.accent.foreground,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 10,
    lineHeight: 13,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  summarySelection: {
    color: semantic.text.primary,
    fontFamily: "Manrope_700Bold",
    fontSize: 12,
    lineHeight: 17,
  },
  emptySummary: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 18,
  },
  folderCard: {
    minHeight: 160,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    gap: 12,
    shadowColor: semantic.text.primary,
    shadowOpacity: 0.08,
    shadowRadius: 12,
    shadowOffset: { width: 0, height: 6 },
  },
  folderCardLocked: {
    opacity: 0.78,
  },
  folderCover: {
    width: 76,
    height: 116,
    borderRadius: 7,
    overflow: "hidden",
    justifyContent: "flex-start",
  },
  folderCoverImage: {
    borderRadius: 7,
  },
  folderCoverScrim: {
    flex: 1,
    backgroundColor: semantic.surface.scrim,
    padding: 8,
  },
  folderCoverText: {
    color: semantic.text.inverse,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 14,
    lineHeight: 16,
    textAlign: "center",
  },
  folderCopy: {
    flex: 1,
    minWidth: 0,
    gap: 7,
  },
  folderTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 17,
    lineHeight: 22,
  },
  folderBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 12,
    lineHeight: 17,
  },
  folderButton: {
    alignSelf: "flex-start",
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: semantic.action.primary,
    paddingHorizontal: 11,
    paddingVertical: 7,
  },
  folderButtonText: {
    color: semantic.action.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 12,
    lineHeight: 16,
  },
  qrBox: {
    width: 82,
    height: 82,
    borderRadius: 8,
    backgroundColor: semantic.surface.raised,
    alignItems: "center",
    justifyContent: "center",
  },
  reviewPanel: {
    gap: 14,
  },
  submissionFeedback: {
    gap: 10,
  },
  reviewHero: {
    borderRadius: 16,
    backgroundColor: semantic.action.primary,
    padding: 18,
    gap: 8,
  },
  reviewTitle: {
    color: semantic.text.inverse,
    fontFamily: "CormorantGaramond_700Bold",
    fontSize: 34,
    lineHeight: 36,
  },
  reviewBody: {
    color: semantic.text.inverse,
    fontFamily: "Manrope_600SemiBold",
    fontSize: 13,
    lineHeight: 20,
  },
  reviewList: {
    gap: 8,
  },
  reviewRow: {
    minHeight: 66,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.raised,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    padding: 10,
  },
  reviewStatus: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: semantic.border.muted,
    alignItems: "center",
    justifyContent: "center",
  },
  reviewStatusComplete: {
    backgroundColor: semantic.action.primary,
    borderColor: semantic.action.primary,
  },
  reviewRowCopy: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  reviewRowTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 17,
  },
  reviewRowBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 16,
  },
  reviewDisclaimer: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 17,
    textAlign: "center",
  },
  directorNotice: {
    marginHorizontal: -16,
    marginBottom: -18,
    borderTopWidth: 1,
    borderColor: semantic.border.subtle,
    backgroundColor: semantic.surface.muted,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 34,
    paddingVertical: 13,
    gap: 10,
  },
  noticeCopy: {
    flex: 1,
    minWidth: 0,
  },
  noticeTitle: {
    color: semantic.text.primary,
    fontFamily: "Manrope_800ExtraBold",
    fontSize: 13,
    lineHeight: 18,
  },
  noticeBody: {
    color: semantic.text.secondary,
    fontFamily: "Manrope_500Medium",
    fontSize: 11,
    lineHeight: 15,
  },
});
